import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import Layout from "./components/Layout";
import { ThemeProvider } from "./lib/theme";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

import Login from "./pages/Login";
import SetupPassword from "./pages/SetupPassword";
import Dashboard from "./pages/Dashboard";
import Sources from "./pages/Sources";
import RawSignals from "./pages/RawSignals";
import Analyses from "./pages/Analyses";
import Insights from "./pages/Insights";
import ContentIdeas from "./pages/ContentIdeas";
import Trends from "./pages/Trends";
import Integrations from "./pages/Integrations";
import Settings from "./pages/Settings";
import Streams from "./pages/Streams";
import StreamsOverview from "./pages/StreamsOverview";
import StreamDetail from "./pages/StreamDetail";
import NarrativeLabs from "./pages/NarrativeLabs";
import HypothesisDetail from "./pages/HypothesisDetail";
import WeeklyBrief from "./pages/WeeklyBrief";
import Workflow from "./pages/Workflow";
import AdminUsers from "./pages/AdminUsers";
import Budget from "./pages/Budget";
import ContentStudio from "./pages/ContentStudio";
import SourceDetail from "./pages/content-studio/SourceDetail";
import { useAuth } from "./lib/auth";

// `/streams` is role-aware: admins get the full management page,
// everyone else gets the read-only "Listening Map" overview.
const StreamsRouter = () => {
    const { isAdmin } = useAuth();
    return isAdmin ? <Streams /> : <StreamsOverview />;
};

// Helper to wrap a page with the standard Layout + Protected gate.
const Authed = ({ children, adminOnly = false }) => (
    <ProtectedRoute adminOnly={adminOnly}>
        <Layout>{children}</Layout>
    </ProtectedRoute>
);

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Toaster richColors position="top-center" />
                    <Routes>
                        {/* Public — login screen has no app chrome */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/setup-password" element={<SetupPassword />} />

                        {/* Shared (any authenticated user) */}
                        <Route path="/" element={<Authed><Dashboard /></Authed>} />
                        <Route path="/weekly-brief" element={<Authed><WeeklyBrief /></Authed>} />
                        <Route path="/analyses" element={<Authed><Analyses /></Authed>} />
                        <Route path="/insights" element={<Authed><Insights /></Authed>} />
                        <Route path="/content-ideas" element={<Authed><ContentIdeas /></Authed>} />
                        <Route path="/labs" element={<Authed><NarrativeLabs /></Authed>} />
                        <Route path="/labs/:id" element={<Authed><HypothesisDetail /></Authed>} />
                        <Route path="/trends" element={<Authed><Trends /></Authed>} />

                        {/* Streams: read-only overview for users, full management for admins */}
                        <Route path="/streams" element={<Authed><StreamsRouter /></Authed>} />

                        {/* Admin only */}
                        <Route path="/workflow" element={<Authed adminOnly><Workflow /></Authed>} />
                        <Route path="/streams/:id" element={<Authed adminOnly><StreamDetail /></Authed>} />
                        <Route path="/sources" element={<Authed adminOnly><Sources /></Authed>} />
                        <Route path="/signals" element={<Authed adminOnly><RawSignals /></Authed>} />
                        <Route path="/integrations" element={<Authed adminOnly><Integrations /></Authed>} />
                        <Route path="/settings" element={<Authed adminOnly><Settings /></Authed>} />
                        <Route path="/admin/users" element={<Authed adminOnly><AdminUsers /></Authed>} />
                        <Route path="/budget" element={<Authed adminOnly><Budget /></Authed>} />
                        <Route path="/content-studio" element={<Authed adminOnly><ContentStudio /></Authed>} />
                        <Route path="/content-studio/sources/:id" element={<Authed adminOnly><SourceDetail /></Authed>} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
